import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { weddingSiteSchema, type Wedding, type WeddingSite } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { Pill } from '@/components/ui/Pill';
import { useEntitlements } from '@/features/shell/entitlements';
import { usePublishSite, useUpdateSite } from './queries';

/**
 * Where the couple writes their public page and decides its address.
 *
 * Saving and publishing are separate buttons on purpose. Editing the travel
 * directions should never risk changing the web address already printed on
 * eighty invitations, and taking the page down should not require discarding an
 * edit in progress.
 */
export function SiteTab() {
  const { t } = useTranslation();
  const { wedding, canWrite } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;

  const update = useUpdateSite(wedId);
  const publish = usePublishSite(wedId);
  const entitlements = useEntitlements(wedId);
  const canPublish = entitlements.data?.features.rsvpSite ?? true;

  const [draft, setDraft] = useState<WeddingSite>(() =>
    weddingSiteSchema.parse(wedding.site ?? {}),
  );
  const [slug, setSlug] = useState(wedding.slug ?? '');

  const set = <K extends keyof WeddingSite>(key: K, value: WeddingSite[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const publicUrl = wedding.slug ? `${origin}/w/${wedding.slug}` : undefined;

  return (
    <section className="flex flex-col gap-8">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-page">{t('nav.site')}</h1>
        <Pill tone={wedding.sitePublished ? 'attending' : 'neutral'}>
          {wedding.sitePublished ? t('site.live') : t('site.draft')}
        </Pill>
      </header>

      {/* The address, and the switch. Kept above the writing, because it is what
          people ask about first and it is the part with consequences. */}
      <div className="panel flex flex-col gap-4 p-4">
        <Field label={t('site.address')}>
          {({ id }) => (
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-caption text-leaf-soft">{origin}/w/</span>
              <TextInput
                id={id}
                className="max-w-[18rem] flex-1"
                placeholder="maria-and-tom"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
              />
              <Button
                variant="secondary"
                loading={publish.isPending}
                disabled={!slug || slug === wedding.slug}
                onClick={() => publish.mutate({ slug })}
              >
                {wedding.slug ? t('site.changeAddress') : t('site.claimAddress')}
              </Button>
            </span>
          )}
        </Field>
        {publish.isError && (
          <p role="alert" className="text-caption text-status-declinedFg">
            {t('site.addressTaken')}
          </p>
        )}

        {publicUrl && (
          <p className="flex flex-wrap items-center gap-3 text-caption text-leaf-soft">
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="text-brass-text underline underline-offset-4"
            >
              {publicUrl}
            </a>
            <Button
              variant="secondary"
              loading={publish.isPending}
              // Taking it down is never gated: a plan lapsing must not trap a
              // published page online with no way to remove it.
              disabled={!canPublish && !wedding.sitePublished}
              onClick={() => publish.mutate({ sitePublished: !wedding.sitePublished })}
            >
              {wedding.sitePublished ? t('site.unpublish') : t('site.publish')}
            </Button>
          </p>
        )}
        <p className="text-caption text-leaf-soft">{t('site.publishHint')}</p>
        {!canPublish && !wedding.sitePublished && (
          <p className="text-caption text-brass-text">{t('site.planNeeded')}</p>
        )}
      </div>

      <div className="flex flex-col gap-6">
        <Field label={t('site.headline')}>
          {({ id }) => (
            <TextInput
              id={id}
              value={draft.headline ?? ''}
              onChange={(e) => set('headline', e.target.value)}
            />
          )}
        </Field>

        <Area
          label={t('site.story')}
          value={draft.story ?? ''}
          onChange={(v) => set('story', v)}
          rows={6}
        />
        <Area
          label={t('site.travel')}
          value={draft.travel ?? ''}
          onChange={(v) => set('travel', v)}
          rows={4}
        />

        <Field label={t('site.dressCode')}>
          {({ id }) => (
            <TextInput
              id={id}
              value={draft.dressCode ?? ''}
              onChange={(e) => set('dressCode', e.target.value)}
            />
          )}
        </Field>

        <Field label={t('site.rsvpDeadline')}>
          {({ id }) => (
            <TextInput
              id={id}
              type="date"
              value={draft.rsvpDeadline ?? ''}
              onChange={(e) => set('rsvpDeadline', e.target.value || undefined)}
            />
          )}
        </Field>

        {/* Comma-separated rather than a repeater: it is a short list typed once,
            and three buttons to add three words is worse than typing them. */}
        <Field label={t('site.mealChoices')} hint={t('site.mealChoicesHint')}>
          {({ id }) => (
            <TextInput
              id={id}
              value={draft.mealChoices.join(', ')}
              onChange={(e) =>
                set(
                  'mealChoices',
                  e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .slice(0, 10),
                )
              }
            />
          )}
        </Field>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={draft.songRequests}
            onChange={(e) => set('songRequests', e.target.checked)}
            className="size-5 accent-leaf"
          />
          <span className="text-body">{t('site.songRequests')}</span>
        </label>

        <Area
          label={t('site.rsvpNote')}
          value={draft.rsvpNote ?? ''}
          onChange={(v) => set('rsvpNote', v)}
          rows={2}
        />
      </div>

      <div className="flex items-center gap-3 border-t border-line pt-4">
        {canWrite && (
          <Button
            loading={update.isPending}
            onClick={() => update.mutate({ ...draft, updatedAt: wedding.updatedAt })}
          >
            {t('common.save')}
          </Button>
        )}
        {update.isSuccess && !update.isPending && (
          <span className="text-caption text-leaf-soft">{t('site.saved')}</span>
        )}
        {update.isError && (
          <span role="alert" className="text-caption text-status-declinedFg">
            {t('common.somethingWrong')}
          </span>
        )}
      </div>
    </section>
  );
}

function Area({
  label,
  value,
  onChange,
  rows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows: number;
}) {
  return (
    <Field label={label}>
      {({ id }) => (
        <textarea
          id={id}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-line bg-paper px-3 py-2 text-body"
        />
      )}
    </Field>
  );
}
